import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Mail, Phone, Smartphone, Users } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Card } from "@shared/ui/card";
import Dashboard from "./dashboard";
import { MemberFilter } from "./memberFilter";
import { Button } from "@shared/ui/button";
import { useAreaList, useGetAllCustomerMemberList, useSubAreaList } from "../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";

interface CustomerMemberData {
  id: string;
  accountNumber: string;
  name: string;
  email: string;
  phone: string;
  premise: string;
  subArea: string;
  area: string;
  department: string;
  status: string;
  communicationChannel: string;
}

interface TransformedCustomerMember {
  id: string;
  consumerNo: string;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  premise: string;
  subArea: string;
  area: string;
  communicationChannel: string;
}

interface CustomerFilters {
  area: string[];
  subArea: string[];
  category: string[];
  subCategory: string[];
  status: string;
}

interface CustomersmemberListProps {
  onSubmit: (payload: any) => void;
  onCancel: () => void;
  form?: any;
  formValues: {
    listName: string;
    tags: string[];
    audience: string;
    segment: string;
    listType: string;
  };
  remoteUtilityId: number;
  preSelectedIds?: string[];
  action?: string;
  filters: CustomerFilters;
  onFilterChange: (filters: CustomerFilters) => void;
}

const ITEMS_PER_PAGE = PAGE_SIZE;

const CustomersmemberList: React.FC<CustomersmemberListProps> = ({
  onSubmit,
  onCancel,
  form,
  formValues,
  remoteUtilityId,
  preSelectedIds = [],
  action,
  filters,
  onFilterChange,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const { data: getAllCustomerMemberList } = useGetAllCustomerMemberList(
    Number(formValues.segment),
    currentPage,
    ITEMS_PER_PAGE,
    searchQuery,
    formValues.listType === "0" ? "STATIC_CONSUMERS" : "DYNAMIC_CONSUMERS",
    filters.area,
    filters.subArea,
    filters.category,
    filters.subCategory,
    filters.status
  );

  const areaList = [
    { label: "Area 1", value: "area1" },
    { label: "Area 2", value: "area2" },
    { label: "Area 3", value: "area3" },
    { label: "Area 4", value: "area4" },
  ];

  const subAreaList = [
    { label: "Sub Area 1", value: "subarea1" },
    { label: "Sub Area 2", value: "subarea2" },
    { label: "Sub Area 3", value: "subarea3" },
    { label: "Sub Area 4", value: "subarea4" },
  ];

  const categoryList = [
    { label: "Category 1", value: "category1" },
    { label: "Category 2", value: "category2" },
    { label: "Category 3", value: "category3" },
    { label: "Category 4", value: "category4" },
  ];

  const subCategoryList = [
    { label: "Sub Category 1", value: "subcategory1" },
    { label: "Sub Category 2", value: "subcategory2" },
    { label: "Sub Category 3", value: "subcategory3" },
    { label: "Sub Category 4", value: "subcategory4" },
  ];

  const statusList = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
    { label: "Pending", value: "pending" },
    { label: "Suspended", value: "suspended" },
  ];

  // Set pre-selected IDs when in edit mode
  useEffect(() => {
    if (action === "edit" && preSelectedIds.length > 0) {
      setSelectedRowIds(preSelectedIds);
    }
  }, [action, preSelectedIds]);

  // Transform API data to match the table structure
  const transformedData: TransformedCustomerMember[] = useMemo(() => {
    return (
      getAllCustomerMemberList?.results?.map((item: any) => ({
        id: item.id,
        consumerNo: item.consumerNo,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        contactNumber: item.contactNumber,
        premise: item.service?.premise || "N/A",
        subArea: item.service?.subArea || "N/A",
        area: item.service?.area || "N/A",
        communicationChannel: item.communicationChannel,
      })) || []
    );
  }, [getAllCustomerMemberList]);

  const selectedRows = useMemo(() => {
    return transformedData.filter((row) => selectedRowIds.includes(row.id));
  }, [selectedRowIds, transformedData]);

  const totalItems = getAllCustomerMemberList?.count || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const pagination = useMemo(() => {
    const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

    return {
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: ITEMS_PER_PAGE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    };
  }, [currentPage, totalItems, totalPages]);

  const dashboardStats = useMemo(() => {
    const totalAvailable = getAllCustomerMemberList?.count || 0;
    const totalSelected = selectedRows?.length || 0;
    const emailOnly =
      selectedRows?.filter(
        (row) => row?.communicationChannel?.toLowerCase() === "email"
      ).length || 0;
    const smsOnly =
      selectedRows?.filter(
        (row) => row?.communicationChannel?.toLowerCase() === "sms"
      ).length || 0;
    const bothChannels =
      selectedRows?.filter(
        (row) => row?.communicationChannel?.toLowerCase() === "both"
      ).length || 0;

    return [
      {
        title: "Total Available",
        count: totalAvailable,
        icon: <Users className="h-8 w-8 text-blue-500" />,
      },
      {
        title: "Total Selected",
        count: totalSelected,
        icon: <Users className="h-8 w-8 text-blue-500" />,
      },
      {
        title: "Email Only",
        count: emailOnly,
        icon: <Mail className="h-8 w-8 text-green-500" />,
      },
      {
        title: "Phone Only",
        count: smsOnly,
        icon: <Phone className="h-8 w-8 text-orange-500" />,
      },
      {
        title: "Both Channels",
        count: bothChannels,
        icon: <Smartphone className="h-8 w-8 text-red-500" />,
      },
    ];
  }, [selectedRows, getAllCustomerMemberList]);

  const handleApplyFilters = (newFilters: CustomerFilters) => {
    onFilterChange(newFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    onFilterChange({
      area: [],
      subArea: [],
      category: [],
      subCategory: [],
      status: "",
    });
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedRowIds(selectedIds);
  };

  const columns: TableColumn<TransformedCustomerMember>[] = [
    {
      key: "consumerNo",
      header: "Account Number",
      type: "text",
    },
    {
      key: "firstName",
      header: "Name",
      type: "custom",
      render: (_: any, row: TransformedCustomerMember) => (
        <span className="font-medium text-gray-900">
          {row.firstName} {row.lastName}
        </span>
      ),
    },
    {
      key: "email",
      header: "Email",
      type: "text",
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value}</span>
      ),
    },
    {
      key: "contactNumber",
      header: "Phone",
      type: "text",
    },
    {
      key: "premise",
      header: "Premise",
      type: "text",
    },
    {
      key: "subArea",
      header: "Sub-area",
      type: "text",
    },
    {
      key: "area",
      header: "Area",
      type: "text",
    },
  ];

  // Build and submit the payload - handles both static and dynamic lists
  const handleSubmitClick = async () => {
    // Validate parent form first
    if (form) {
      const isValid = await form.trigger();
      if (!isValid) {
        return;
      }
    }

    const isStaticList = formValues.listType === "0";
    const isDynamicList = formValues.listType === "1";

    // Validation based on list type
    if (isStaticList && selectedRowIds.length === 0) {
      toast({
        title: "No Members Selected",
        description: "Please select at least one member for static list.",
        variant: "destructive",
      });
      return;
    }

    if (isDynamicList) {
      const hasFilters = 
        filters.area.length > 0 || 
        filters.subArea.length > 0 || 
        filters.category.length > 0 || 
        filters.subCategory.length > 0 || 
        filters.status;
      
      if (!hasFilters) {
        toast({
          title: "No Filters Applied",
          description: "Please apply at least one filter for dynamic list.",
          variant: "destructive",
        });
        return;
      }
    }

    // Base payload structure
    const basePayload = {
      remote_utility_id: remoteUtilityId,
      name: formValues.listName,
      audience_type: Number(formValues.audience),
      filter_type: Number(formValues.listType),
      segment: Number(formValues.segment) || undefined,
      tags: formValues.tags,
    };

    let payload: any;

    if (isStaticList) {
      // Static list - include recipient_ids
      payload = {
        ...basePayload,
        recipient_data: {
          recipient_ids: selectedRowIds,
        },
      };
    } else {
      // Dynamic list - include filters as arrays
      const dynamicFilters: any = {};

      // Only include non-empty filters as arrays
      if (filters.area.length > 0) {
        dynamicFilters.area = filters.area;
      }
      if (filters.subArea.length > 0) {
        dynamicFilters.subarea = filters.subArea;
      }
      if (filters.category.length > 0) {
        dynamicFilters.category = filters.category;
      }
      if (filters.subCategory.length > 0) {
        dynamicFilters.sub_category = filters.subCategory;
      }
      if (filters.status) {
        dynamicFilters.status = filters.status;
      }

      payload = {
        ...basePayload,
        filters: dynamicFilters,
      };
    }

    console.log("Customer Payload:", payload);
    console.log("List Type:", isStaticList ? "STATIC" : "DYNAMIC");
    if (isStaticList) {
      console.log("Total Recipients Selected:", selectedRowIds.length);
    } else {
      console.log("Filters Applied:", payload.filters);
    }

    // Pass the complete payload to parent
    onSubmit(payload);
  };

  const handleCancelClick = () => {
    onCancel();
  };

  const filterPanel = (
    <MemberFilter
      area={filters.area}
      subArea={filters.subArea}
      category={filters.category}
      subCategory={filters.subCategory}
      status={filters.status}
      areaList={areaList}
      subAreaList={subAreaList}
      categoryList={categoryList}
      subCategoryList={subCategoryList}
      statusList={statusList}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
    />
  );

  const hasActiveFilters = 
    filters.area.length > 0 || 
    filters.subArea.length > 0 || 
    filters.category.length > 0 || 
    filters.subCategory.length > 0 || 
    filters.status;
  
  const activeFilterCount = [
    filters.area.length > 0,
    filters.subArea.length > 0,
    filters.category.length > 0,
    filters.subCategory.length > 0,
    !!filters.status,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6 p-1">
      <Card className="mt-1">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {formValues.listType === "0" 
              ? "Select Members (Static List)" 
              : "Configure Filters (Dynamic List)"}
          </h2>
          <p className="text-sm text-gray-500">
            {formValues.listType === "0"
              ? "Select specific recipients to include in this static list"
              : "Set filters to automatically include recipients matching criteria"}
          </p>
        </div>
        <Dashboard statsData={dashboardStats} />
        <div className="mt-2">
          <GenericTable
            data={transformedData}
            columns={columns}
            rowKey="id"
            selectable={formValues.listType === "0"}
            selectedRows={selectedRowIds}
            onSelectionChange={handleSelectionChange}
            emptyMessage="No customers found matching your criteria"
            pagination={pagination}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            searchPlaceholder="Search by name, account number, email or phone..."
            searchDebounceMs={300}
            filterPanel={filterPanel}
            filters={
              hasActiveFilters ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {activeFilterCount} filter(s) active
                  </span>
                </div>
              ) : null
            }
          />
        </div>
      </Card>
      <div className="flex justify-end">
        <Button variant="default" onClick={handleCancelClick} className="mr-2">
          Cancel
        </Button>
        <Button onClick={handleSubmitClick}>
          {action === "edit" ? "Update" : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default CustomersmemberList;